import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../components/shared/AuthContext';
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import Loader from '../../components/ui/Loader';
import ReportHeader from "../../components/reports/ReportHeader";

const fmt = (n) => (n != null ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—");
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;
  } catch {
    return d;
  }
};

const FutureDemandReport = () => {
  const { data: authData, roleName } = useAuth();
  const branchId = authData?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [balanceMap, setBalanceMap] = useState({});

  // Summary counters
  const [summary, setSummary] = useState({
    totalRows: 0, totalPrincipal: 0, totalInterest: 0, totalEMI: 0, totalOutstanding: 0,
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (!isSuperAdmin && branchId) params.branchId = branchId;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const res = await axios.get("/api/v2/future-demand-report", {
        baseURL: API_BASE_URL,
        params,
      });

      let merged = Array.isArray(res.data?.data) ? res.data.data : [];

      // Sort by dueDate ascending
      merged.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

      setRows(merged);
      setSummary({
        totalRows: merged.length,
        totalPrincipal: merged.reduce((s, r) => s + Number(r.principal || 0), 0),
        totalInterest: merged.reduce((s, r) => s + Number(r.interest || 0), 0),
        totalEMI: merged.reduce((s, r) => s + Number(r.total || 0), 0),
        totalOutstanding: merged.reduce((s, r) => s + Number(r.outstanding || 0), 0),
      });

      // Fetch closing balances (total savings: deposits - withdrawals) for all members
      const uniqueCuids = [...new Set(merged.map((r) => r.customer_unique_id).filter((c) => c && c !== "—"))];
      if (uniqueCuids.length > 0) {
        try {
          const balRes = await axios.post("/api/v2/member-closing-balances", {
            customerUniqueIds: uniqueCuids.join(",")
          }, {
            baseURL: API_BASE_URL
          });
          setBalanceMap(balRes.data?.data || {});
        } catch (e) {
          console.error("Balance fetch error:", e);
        }
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [isSuperAdmin, branchId, fromDate, toDate]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = rows.filter((r) =>
    String(r.customer_name || "").toLowerCase().includes(search.toLowerCase()) ||
    String(r.customer_unique_id || "").toLowerCase().includes(search.toLowerCase()) ||
    String(r.account_no || "").toLowerCase().includes(search.toLowerCase()) ||
    String(r.contact_no || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const exportCSV = () => {
    const headers = [
      "#", "Customer Name", "Customer ID", "Account No", "Contact No",
      "Due No", "Due Date", "Principal (₹)", "Interest (₹)", "EMI Amount (₹)",
      "Arrear Principal (₹)", "Arrear Interest (₹)", "Arrear Total (₹)",
      "Outstanding (₹)", "Penalty (₹)", "Status", "Closing Balance (₹)",
      "Employee Name", "Employee Code",
    ];
    const csvRows = [
      headers.join(","),
      ...filtered.map((r, i) => [
        i + 1,
        `"${r.customer_name}"`,
        r.customer_unique_id,
        r.account_no,
        r.contact_no,
        r.dueNo,
        fmtDate(r.dueDate),
        r.principal || 0,
        r.interest || 0,
        r.total || 0,
        r.arrearPrincipal || 0,
        r.arrearInterest || 0,
        r.arrearTotal || 0,
        r.outstanding || 0,
        r.penalty || 0,
        r.received || "Pending",
        balanceMap[r.customer_unique_id] ?? 0,
        r.employee_name || "—",
        r.employee_code || "—",
      ].join(",")),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "future_demand_report.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      {/* Top Filter and Actions Bar using reusable ReportHeader */}
      <ReportHeader
        filters={
          <>
            {/* Due Date From */}
            <div className="w-[145px] mt-1">
              <DatePicker
                label="Due Date From"
                selected={fromDate}
                onChange={(date) => setFromDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* Due Date To */}
            <div className="w-[145px] mt-1">
              <DatePicker
                label="Due Date To"
                selected={toDate}
                onChange={(date) => setToDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* Apply Button */}
            <div className="mt-1">
              <Button onClick={loadData} variant="secondary" className="h-[38px] px-3 font-semibold text-xs">
                Apply Filters
              </Button>
            </div>

            {/* Search Input */}
            <div className="min-w-[240px] mt-1">
              <input
                placeholder="Search name / ID / account / contact..."
                value={search}
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                className="w-full bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[38px] text-xs px-3.5 focus:outline-none transition-all font-medium"
              />
            </div>
          </>
        }
        actions={
          <Button
            onClick={exportCSV}
            variant="primary"
            size="sm"
            className="shadow-sm h-[38px] px-4 font-bold"
          >
            <i className="fa-solid fa-file-csv mr-1.5"></i>
            Export CSV
          </Button>
        }
      />
      {loading && !rows.length && <Loader message="Loading future demand data..." />}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3.5 mb-4">
        {[
          {
            label: "Pending EMIs",
            val: summary.totalRows,
            bg: "bg-[#DCEBFF]",
            border: "border-[#2563EB]/25",
            accent: "text-[#2563EB]",
            iconBg: "bg-white/80",
            icon: "fa-users",
          },
          {
            label: "Total Principal",
            val: `₹${fmt(summary.totalPrincipal)}`,
            bg: "bg-[#DDF5E6]",
            border: "border-[#16A34A]/25",
            accent: "text-[#16A34A]",
            iconBg: "bg-white/80",
            icon: "fa-money-bill-wave",
          },
          {
            label: "Total Interest",
            val: `₹${fmt(summary.totalInterest)}`,
            bg: "bg-[#E9DFFF]",
            border: "border-[#7C3AED]/25",
            accent: "text-[#7C3AED]",
            iconBg: "bg-white/80",
            icon: "fa-percent",
          },
          {
            label: "Total EMI Demand",
            val: `₹${fmt(summary.totalEMI)}`,
            bg: "bg-[#D9F3F7]",
            border: "border-[#0891B2]/25",
            accent: "text-[#0891B2]",
            iconBg: "bg-white/80",
            icon: "fa-file-invoice-dollar",
          },
          {
            label: "Total Outstanding",
            val: `₹${fmt(summary.totalOutstanding)}`,
            bg: "bg-[#FFF0C7]",
            border: "border-[#D97706]/25",
            accent: "text-[#D97706]",
            iconBg: "bg-white/80",
            icon: "fa-clock",
          },
        ].map((c, idx) => (
          <div
            key={idx}
            className={`${c.bg} ${c.border} border py-2.5 px-3.5 rounded-xl shadow-xs flex items-center justify-between transition-all hover:shadow-sm`}
          >
            <div className="flex flex-col min-w-0 pr-2">
              <span className={`text-[10px] font-bold ${c.accent} uppercase tracking-wider mb-1 truncate`} title={c.label}>
                {c.label}
              </span>
              <span className={`text-base md:text-lg font-black truncate ${c.accent}`}>
                {c.val}
              </span>
            </div>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm flex-shrink-0 ${c.iconBg} ${c.accent} shadow-xs`}>
              <i className={`fa-solid ${c.icon}`}></i>
            </div>
          </div>
        ))}
      </div>

      {/* Main Table */}
      <div className="flex-grow flex flex-col min-h-0">
        <Table>
          <Table.Head>
            <tr>
              {[
                "#", "Customer Name", "Customer ID", "Account No", "Contact No",
                "Due No", "Due Date",
                "Principal", "Interest", "EMI Amount",
                "Arrear Principal", "Arrear Interest", "Arrear Total",
                "Outstanding", "Penalty", "Status", "Closing Balance",
                "Employee Name", "Employee Code",
              ].map((c) => (
                <Table.HeadCell key={c} align={c === "Due Date" || c === "Status" ? "center" : c.includes("(") || c.includes("Amount") || c.includes("Principal") || c.includes("Interest") || c.includes("Total") || c.includes("Outstanding") || c.includes("Penalty") || c.includes("Balance") ? "right" : "left"}>
                  {c}
                </Table.HeadCell>
              ))}
            </tr>
          </Table.Head>
          <Table.Body>
            {paginated.length === 0 ? (
              <Table.EmptyRow colSpan={19} message="No future demand records found." />
            ) : (
              paginated.map((r, i) => {
                const hasArrear = Number(r.arrearTotal || 0) > 0;
                return (
                  <Table.Row key={r.id || i} className={hasArrear ? "bg-amber-50/20" : ""}>
                    <Table.Cell align="left" className="text-ink-400">{(currentPage - 1) * rowsPerPage + i + 1}</Table.Cell>
                    <Table.Cell align="left" className="font-semibold text-ink-900 whitespace-nowrap">{r.customer_name}</Table.Cell>
                    <Table.Cell align="left" className="font-semibold text-primary">{r.customer_unique_id}</Table.Cell>
                    <Table.Cell align="left" className="font-mono text-xs">{r.account_no}</Table.Cell>
                    <Table.Cell align="left">{r.contact_no}</Table.Cell>
                    <Table.Cell align="left" className="font-semibold">{r.dueNo}</Table.Cell>
                    <Table.Cell align="center" className="font-semibold text-indigo-600 whitespace-nowrap">{fmtDate(r.dueDate)}</Table.Cell>
                    <Table.Cell align="right">₹{fmt(r.principal)}</Table.Cell>
                    <Table.Cell align="right">₹{fmt(r.interest)}</Table.Cell>
                    <Table.Cell align="right" className="font-bold text-emerald-600">₹{fmt(r.total)}</Table.Cell>
                    <Table.Cell align="right" className={hasArrear ? "text-rose-600" : "text-ink-400"}>₹{fmt(r.arrearPrincipal)}</Table.Cell>
                    <Table.Cell align="right" className={hasArrear ? "text-rose-600" : "text-ink-400"}>₹{fmt(r.arrearInterest)}</Table.Cell>
                    <Table.Cell align="right" className={hasArrear ? "font-bold text-rose-600" : "text-ink-400"}>₹{fmt(r.arrearTotal)}</Table.Cell>
                    <Table.Cell align="right" className="text-amber-600">₹{fmt(r.outstanding)}</Table.Cell>
                    <Table.Cell align="right" className={Number(r.penalty) > 0 ? "text-rose-600" : "text-ink-400"}>₹{fmt(r.penalty)}</Table.Cell>
                    <Table.Cell align="center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
                        {r.received || "Pending"}
                      </span>
                    </Table.Cell>
                    <Table.Cell align="right" className="font-bold text-[#0369a1]">
                      ₹{fmt(balanceMap[r.customer_unique_id] ?? 0)}
                    </Table.Cell>
                    <Table.Cell align="left" className="whitespace-nowrap">
                      <span className="bg-indigo-50 text-indigo-700 border border-indigo-100 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                        {r.employee_name || "—"}
                      </span>
                    </Table.Cell>
                    <Table.Cell align="left" className="text-xs text-ink-500">{r.employee_code || "—"}</Table.Cell>
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="bg-canvas-muted border-t border-border-medium font-bold text-ink-800 text-sm">
                <td colSpan={7} className="p-3 text-right">Totals:</td>
                <td className="p-3 text-right">₹{fmt(filtered.reduce((s, r) => s + Number(r.principal || 0), 0))}</td>
                <td className="p-3 text-right">₹{fmt(filtered.reduce((s, r) => s + Number(r.interest || 0), 0))}</td>
                <td className="p-3 text-right text-emerald-600 font-black">₹{fmt(filtered.reduce((s, r) => s + Number(r.total || 0), 0))}</td>
                <td className="p-3 text-right">₹{fmt(filtered.reduce((s, r) => s + Number(r.arrearPrincipal || 0), 0))}</td>
                <td className="p-3 text-right">₹{fmt(filtered.reduce((s, r) => s + Number(r.arrearInterest || 0), 0))}</td>
                <td className="p-3 text-right text-rose-600">₹{fmt(filtered.reduce((s, r) => s + Number(r.arrearTotal || 0), 0))}</td>
                <td className="p-3 text-right text-amber-600">₹{fmt(filtered.reduce((s, r) => s + Number(r.outstanding || 0), 0))}</td>
                <td className="p-3 text-right">₹{fmt(filtered.reduce((s, r) => s + Number(r.penalty || 0), 0))}</td>
                <td colSpan={4} className="p-3" />
              </tr>
            </tfoot>
          )}
        </Table>

        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between mt-2 bg-white px-3 py-1.5 rounded-lg border border-border-light shadow-xs text-xs">
            <div className="flex items-center gap-2 text-xs text-ink-500">
              <span className="font-medium">Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="bg-transparent border border-border-medium rounded px-1.5 py-0.5 h-[26px] focus:outline-none font-semibold text-ink-700 text-xs"
              >
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <span className="text-xs text-ink-500 font-medium">
              Showing {paginated.length} of {filtered.length} records
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="h-[26px] px-2 text-xs font-semibold"
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="h-[26px] px-2 text-xs font-semibold"
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="h-[26px] px-2 text-xs font-semibold"
              >
                Next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="h-[26px] px-2 text-xs font-semibold"
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default FutureDemandReport;
